import { useEffect, useRef, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import styled, { css, keyframes } from 'styled-components';

const tooltipFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0px);
  }
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
`;

const CustomTooltip = styled.div`
  opacity: 0;
  position: relative;
  margin-bottom: 8px;
  background: #fff;
  color: #222;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.18);
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
  border: 1px solid #eee;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #fff;
    filter: drop-shadow(0 2px 2px rgba(0,0,0,0.18));
  }

  ${({ visible }) => visible && css`
    opacity: 1;
    animation: ${tooltipFadeIn} 0.2s ease-in forwards;
  `}
`;

const BotButtonWrapper = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  background-color: #62bb46;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  transition: background-color 0.3s ease;
  border: none;
  outline: none;
  padding: 0;

  ${({ isRectangle }) =>
    isRectangle
      ? css`
          padding: 0 16px;
          height: 35px;
          border-radius: 20px;
        `
      : css`
          width: 50px;
          height: 50px;
          border-radius: 50%;
        `}

`;

const BotImageIcon = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const BotContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`;

const BotTitle = styled.span`
  margin-left: 8px;
  font-size: 14px;
  font-weight: bold;
`;

const CommentIcon = styled(FontAwesome)`
  font-size: 20px;
  color: #fff;
`;


/**
 * Renders a button with an image that serves as the bot launcher icon. If the image is not present, it will render the title with a fontawesome icon.
 * Shows a tooltip bubble with configurable delay and hover/touch logic.
 * @param {object} props - The component props.
 * @param {string} [props.launcherIcon] - The URL of the image to use as the bot launcher icon.
 * @param {string} [props.title] - The name which should be displayed if the launcher icon is not present.
 * @param {string} [props.bubbleText] - The text to display inside the tooltip bubble.
 * @param {number} [props.bubbleDelay=0] - The delay in seconds before showing the tooltip bubble automatically.
 * @param {function} props.handleButtonClick - The function to call when the button is clicked.
 * @returns {JSX.Element} - The rendered button with the bot launcher icon.
 */
const BotIcon = ({
  launcherIcon,
  handleButtonClick,
  title,
  bubbleText,
  bubbleDelay = 0
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const tooltipTimeoutRef = useRef(null);

  // Show tooltip automatically after delay
  useEffect(() => {
    if (!bubbleText || bubbleText.trim() === '') {
      setShowTooltip(false);
      return;
    }

    // Clear any previous timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }

    if (!isUserInteracting) {
      if (!bubbleDelay) {
        setShowTooltip(true);
      } else {
        tooltipTimeoutRef.current = setTimeout(() => {
          if (!isUserInteracting) {
            setShowTooltip(true);
          }
        }, bubbleDelay * 1000);
      }
    }

    // Cleanup on unmount
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, [bubbleText, bubbleDelay, isUserInteracting]);

  // Handlers for hover/touch
  const handleMouseEnter = () => {
    setIsUserInteracting(true);
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (bubbleText) setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setIsUserInteracting(false);
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  return (
    <ButtonContainer>
      <CustomTooltip id="bot-tooltip" role="tooltip" visible={showTooltip && bubbleText}>{bubbleText}</CustomTooltip>
      <BotButtonWrapper
        isRectangle={!launcherIcon && !!title}
        id="lex-web-ui"
        aria-describedby={showTooltip ? 'bot-tooltip' : undefined}
        onClick={handleButtonClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseEnter}
        onTouchEnd={handleMouseLeave}
        onTouchCancel={handleMouseLeave}
        aria-label="show chat window"
      >
        {launcherIcon ? (
          <BotImageIcon src={launcherIcon} alt={title || 'Message Icon'} />
        ) : (
          <BotContent>
            <CommentIcon name="comment" />
            {title && <BotTitle>{title}</BotTitle>}
          </BotContent>
        )}
      </BotButtonWrapper>
    </ButtonContainer>
  );
};

export default BotIcon;